import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./KeptPaywall.module.scss"

/**
 * KeptPaywall (`/kept`) — an original, self-contained NON-ONBOARDING upsell for
 * the real Cloud Vault automatic backup feature ("secure, full-resolution
 * backups of every photo and video").
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/kept.md
 * Nothing among the 24 prior paywalls does this combination:
 *   - Layout:      a horizontal "keeper line" — everyday photos streaming across a
 *                  track through a seal head. NOT a centered hero+card, table, chat,
 *                  receipt, map, calendar, road, editor, player, or network diagram.
 *   - Interaction: PRESS-AND-HOLD to seal (hold-to-commit). No prior paywall used a
 *                  held, charging gesture; the payoff is a contrast reveal — you had
 *                  to hold it the whole way; Premium holds it for you, automatically.
 *   - Register:    calm / reassuring-confident (not fear, luxury, playful, nostalgia).
 *   - Art:         light, editorial, photographic (Unsplash everyday-life memories);
 *                  brand blue = the KEPT/protected state, grey = not-yet-backed-up.
 *   - Value frame: peace-of-mind via COMMITMENT + CONTRAST — "never lose a memory,
 *                  kept automatically in full quality." Distinct from Reflex's speed-
 *                  to-hide and Deep Freeze's storage/loss.
 *
 * Honest & App Store compliant: automatic full-resolution Cloud Vault backup is a
 * real Premium feature. No data-loss threat ("not backed up" ≠ "deleted"), no fake
 * countdown, no fake scarcity, real savings arithmetic. Yearly is the featured,
 * default plan and carries the largest price; monthly is small and secondary. The
 * sample images are labelled as Unsplash demo photos, not the user's library.
 */

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

type PlanKey = "yearly" | "monthly"

// The visible slice of the "keeper line" — a warm mix of everyday memories.
// The counter speaks for the whole library; these six ride across the track.
const TILES = [
  { src: "/kept/picnic.jpg", tag: "Sun 4:12pm" },
  { src: "/kept/grandma.jpg", tag: "Dad & Mia" },
  { src: "/kept/birthday.jpg", tag: "Mia turns 5" },
  { src: "/kept/dog.jpg", tag: "Biscuit" },
  { src: "/kept/hike.jpg", tag: "Ridge trail" },
  { src: "/kept/beach.jpg", tag: "Golden hour" },
]

const KeptPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  // Library size for the counter — prefer real debug data, fall back to a number.
  const total = useMemo(() => {
    const n = state.productData?.numPhotos
    return n && n > 200 ? n : 2148
  }, [state.productData])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── The hold-to-seal mechanic ────────────────────────────────────────────
  // p: 0 = nothing backed up, 1 = the whole line sealed.
  const [p, setP] = useState(0)
  const [holding, setHolding] = useState(false)
  const [sealed, setSealed] = useState(false)
  const [demoed, setDemoed] = useState(false)

  const pRef = useRef(0)
  const targetRef = useRef(0)
  const rampRef = useRef(2400) // ms for a full 0→1 charge
  const dirRef = useRef(0) // 0 idle, 1 charging
  const rafRef = useRef<number | undefined>(undefined)
  const lastTsRef = useRef<number | null>(null)
  const downTsRef = useRef(0)

  const setBoth = (v: number) => {
    pRef.current = v
    setP(v)
  }

  const tick = useCallback((ts: number) => {
    if (lastTsRef.current == null) lastTsRef.current = ts
    const dt = ts - lastTsRef.current
    lastTsRef.current = ts

    let np = pRef.current + (dt / rampRef.current) * dirRef.current
    if (np >= targetRef.current) {
      np = targetRef.current
      dirRef.current = 0
    }
    np = clamp(np, 0, 1)
    pRef.current = np
    setP(np)

    if (np >= 1) {
      dirRef.current = 0
      setSealed(true)
      setHolding(false)
    }

    if (dirRef.current === 1) {
      rafRef.current = requestAnimationFrame(tick)
    } else {
      rafRef.current = undefined
      lastTsRef.current = null
    }
  }, [])

  const chargeTo = useCallback(
    (target: number, ramp: number) => {
      targetRef.current = target
      rampRef.current = ramp
      dirRef.current = 1
      lastTsRef.current = null
      if (rafRef.current == null) rafRef.current = requestAnimationFrame(tick)
    },
    [tick],
  )

  const stopCharge = useCallback(() => {
    dirRef.current = 0
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    rafRef.current = undefined
    lastTsRef.current = null
  }, [])

  // Auto-demo once: the line seals part-way on its own, then hands you the gesture.
  useEffect(() => {
    if (reduceMotion) {
      setBoth(1)
      setSealed(true)
      setDemoed(true)
      return
    }
    const t = window.setTimeout(() => {
      if (pRef.current === 0) chargeTo(0.34, 3200)
    }, 650)
    return () => {
      window.clearTimeout(t)
      stopCharge()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onDown = useCallback(
    (e: React.PointerEvent) => {
      if (sealed) return
      setDemoed(true)
      setHolding(true)
      downTsRef.current =
        typeof performance !== "undefined" ? performance.now() : 0
      try {
        e.currentTarget.setPointerCapture(e.pointerId)
      } catch {
        /* no-op */
      }
      chargeTo(1, 2400)
    },
    [sealed, chargeTo],
  )

  const onUp = useCallback(() => {
    if (sealed) return
    setHolding(false)
    const now = typeof performance !== "undefined" ? performance.now() : 0
    const held = now - downTsRef.current
    // A quick tap (or keyboard activate) completes the seal; a real hold pauses.
    if (held < 180 && pRef.current < 1) {
      chargeTo(1, 900)
    } else {
      stopCharge()
    }
  }, [sealed, chargeTo, stopCharge])

  const onKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (sealed) return
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        setDemoed(true)
        chargeTo(1, 900)
      }
    },
    [sealed, chargeTo],
  )

  const kept = Math.round(p * total)
  const tilesKept = TILES.map((_, i) => p >= (i + 0.5) / TILES.length)
  const pct = Math.round(p * 100)

  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  return (
    <div className={styles.root}>
      <div className={styles.grain} aria-hidden="true" />

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9.2 12.1l2 2 3.6-3.9"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <div className={styles.intro}>
        <span className={styles.eyebrow}>Cloud Vault · Premium</span>
        <h1 className={styles.title}>
          <span
            className={`${styles.titleLine} ${sealed ? styles.titleOut : styles.titleIn}`}
          >
            A memory you didn&#8217;t back up
            <br />
            is a memory you&#8217;re trusting to luck.
          </span>
          <span
            className={`${styles.titleLine} ${styles.titleOverlay} ${sealed ? styles.titleIn : styles.titleOut}`}
            aria-hidden={!sealed}
          >
            Every memory, kept.
            <br />
            <span className={styles.titleAccent}>
              Automatically. Full quality.
            </span>
          </span>
        </h1>
      </div>

      {/* ── THE KEEPER LINE ───────────────────────────────────────────────── */}
      <div className={styles.stage}>
        <div className={styles.counterRow}>
          <span className={styles.counterNum}>{kept.toLocaleString()}</span>
          <span className={styles.counterOf}>
            of {total.toLocaleString()} kept
          </span>
          <span
            className={`${styles.counterBadge} ${sealed ? styles.counterBadgeOn : ""}`}
          >
            {sealed ? "All backed up" : `${pct}%`}
          </span>
        </div>

        <div
          className={`${styles.track} ${sealed ? styles.trackSealed : ""}`}
          role="img"
          aria-label={`${kept} of ${total} photos backed up`}
        >
          <div className={styles.rail}>
            {TILES.map((t, i) => (
              <figure
                key={t.src}
                className={`${styles.tile} ${tilesKept[i] ? styles.tileKept : ""}`}
                style={{ ["--i" as any]: i }}
              >
                <img src={t.src} alt="" loading="eager" draggable={false} />
                <span className={styles.tileShade} aria-hidden="true" />
                <figcaption className={styles.tileTag}>{t.tag}</figcaption>
                <span className={styles.tileSeal} aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="12" height="12">
                    <path
                      d="M5 12.5l4.2 4.2L19 7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className={styles.tileState}>
                  {tilesKept[i] ? "Kept" : "Not backed up"}
                </span>
              </figure>
            ))}
          </div>

          {/* the seal head sweeping across the line */}
          <div
            className={styles.sweep}
            style={{ left: `${p * 100}%`, opacity: p > 0 && p < 1 ? 1 : 0 }}
            aria-hidden="true"
          >
            <span className={styles.sweepGlow} />
            <span className={styles.sweepLine} />
          </div>
        </div>

        {/* the hold bar */}
        <button
          type="button"
          className={`${styles.hold} ${holding ? styles.holdOn : ""} ${sealed ? styles.holdDone : ""}`}
          onPointerDown={onDown}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onPointerLeave={holding ? onUp : undefined}
          onKeyDown={onKey}
          aria-label="Press and hold to back up your library"
        >
          <span
            className={styles.holdFill}
            style={{ transform: `scaleX(${holding || sealed ? p : 0})` }}
            aria-hidden="true"
          />
          <span className={styles.holdInner}>
            {sealed ? (
              <>
                <span className={styles.holdIcon} aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="16" height="16">
                    <path
                      d="M5 12.5l4.2 4.2L19 7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                Your whole library is safe
              </>
            ) : holding ? (
              "Keep holding…"
            ) : (
              <>
                <span className={styles.holdIcon} aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="15" height="15">
                    <rect
                      x="4.5"
                      y="10.3"
                      width="15"
                      height="9.4"
                      rx="2.1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.9"
                    />
                    <path
                      d="M8 10.3V8a4 4 0 018 0v2.3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.9"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                Press &amp; hold to back it all up
              </>
            )}
          </span>
        </button>

        <p
          className={`${styles.reveal} ${sealed ? styles.revealOn : ""}`}
          aria-live="polite"
        >
          {sealed ? (
            <>
              You had to hold it the whole way.{" "}
              <strong>Premium holds it for you</strong> — every new photo backed
              up the moment you take it.
            </>
          ) : (
            <>
              {demoed ? "Let go and it stops. " : ""}On Free, backups only
              happen when you remember. Hold to see your line get safe.
            </>
          )}
        </p>
      </div>

      {/* what Premium keeps — honest, real features; three lines, not a perk grid */}
      <ul className={styles.keeps}>
        <li>
          <span className={styles.keepMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="12" height="12">
              <path
                d="M5 12.5l4.2 4.2L19 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Full-resolution originals — never squeezed or downscaled
        </li>
        <li>
          <span className={styles.keepMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="12" height="12">
              <path
                d="M5 12.5l4.2 4.2L19 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Backed up automatically, the moment the shutter clicks
        </li>
        <li>
          <span className={styles.keepMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="12" height="12">
              <path
                d="M5 12.5l4.2 4.2L19 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Private &amp; encrypted — the vault only opens for you
        </li>
      </ul>

      {/* ── PRICING: yearly featured ─────────────────────────────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>Keep everything · Yearly</span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPerMo}>
            about {yearlyPerMonth}/mo, billed once a year
          </span>
        </button>

        <button
          className={`${styles.monthly} ${plan === "monthly" ? styles.monthlySel : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <p className={styles.credit}>Demo photos from Unsplash</p>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? hasTrial
                ? "Keep every memory — start free"
                : "Keep every memory"
              : "Keep every memory monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? hasTrial
                ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
                : `${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default KeptPaywall
